import { Box } from '@ardoq/layout';
import { Text } from '@ardoq/typography';

const isMacOS = (): boolean => window.navigator.userAgent.includes('Mac');

type ShortcutKey = {
  symbol: string;
  readable: string;
};

const getShortcutKeyMap = (): Record<string, ShortcutKey> => {
  return {
    mod: isMacOS()
      ? { symbol: '⌘', readable: 'Command' }
      : { symbol: 'Ctrl', readable: 'Control' },
    alt: isMacOS()
      ? { symbol: '⌥', readable: 'Option' }
      : { symbol: 'Alt', readable: 'Alt' },
    shift: isMacOS()
      ? { symbol: '⇧', readable: 'Shift' }
      : { symbol: 'Shift', readable: 'Shift' },
  };
};

const getShortcutKey = (key: string): ShortcutKey =>
  getShortcutKeyMap()[key.toLowerCase()] || { symbol: key, readable: key };

const getShortcutKeys = (keys: string[]): ShortcutKey[] =>
  keys.map(getShortcutKey);

const getSymbolShortcutKeys = (keys: string[]): string[] =>
  keys.map(getShortcutKey).map(shortcut => shortcut.symbol);

export const getSymbolShortcutString = (keys: string[]): string =>
  isMacOS()
    ? getSymbolShortcutKeys(keys).join('')
    : ` (${getSymbolShortcutKeys(keys).join('+')})`;

const getReadableShortcutKeys = (keys: string[]): string[] =>
  keys.map(getShortcutKey).map(shortcut => shortcut.readable);

export const ShortcutKeyCaption = ({ keys, ...props }: { keys: string[] }) => {
  const modifiedKeys = getShortcutKeys(keys);
  const ariaLabel = getReadableShortcutKeys(keys).join(' + ');

  return (
    <Box paddingLeft="large">
      <Text
        aria-label={ariaLabel}
        {...props}
        variant="caption"
        color="textSubtle"
      >
        {modifiedKeys.map((shortcut, index) => (
          <span key={shortcut.symbol} {...props}>
            {shortcut.symbol}
            <span>{index < modifiedKeys.length - 1 && !isMacOS() && '+'}</span>
          </span>
        ))}
      </Text>
    </Box>
  );
};
